import { FormEvent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Drawer } from './Drawer';
import { Field, Select, TextInput } from './Field';
import { RecordingScheduleGrid } from './RecordingScheduleGrid';
import { camerasApi } from '@api/cameras';
import { HttpError } from '@api/http';
import type {
  Camera,
  CameraGroup,
  CameraType,
  RecordingMode,
  RecordingScheduleSlots,
  StorageTarget,
} from '@api/types';
import { createEmptyRecordingScheduleSlots } from '@lib/recording-schedule';

interface Props {
  open: boolean;
  groups: CameraGroup[];
  onClose: () => void;
  onSaved: (camera: Camera) => void;
}

interface FormState {
  name: string;
  rtspUrl: string;
  groupId: string;
  type: CameraType;
  recordingMode: RecordingMode;
  recordingEnabled: boolean;
  storageTarget: StorageTarget;
}

const DEFAULT_STATE: FormState = {
  name: '',
  rtspUrl: '',
  groupId: '',
  type: 'standard',
  recordingMode: 'continuous',
  recordingEnabled: true,
  storageTarget: 'local',
};

const RTSP_RE = /^rtsps?:\/\/.+/i;
const NAME_MAX = 100;

type FieldErrors = Partial<Record<keyof FormState, string>>;

const browserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';

export function CameraFormDrawer({ open, groups, onClose, onSaved }: Props): JSX.Element {
  const [form, setForm] = useState<FormState>(DEFAULT_STATE);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleSlots, setScheduleSlots] = useState<RecordingScheduleSlots>(() =>
    createEmptyRecordingScheduleSlots(),
  );

  useEffect(() => {
    if (!open) return;
    setForm(DEFAULT_STATE);
    setErrors({});
    setSubmitError(null);
    setSubmitting(false);
    setScheduleSlots(createEmptyRecordingScheduleSlots());
  }, [open]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const name = form.name.trim();
    if (!name) next.name = 'Name is required';
    else if (name.length > NAME_MAX) next.name = `Name must be ≤ ${NAME_MAX} characters`;

    const rtsp = form.rtspUrl.trim();
    if (!rtsp) next.rtspUrl = 'RTSP URL is required';
    else if (!RTSP_RE.test(rtsp)) next.rtspUrl = 'Must start with rtsp:// or rtsps://';

    return next;
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    const payload = {
      name: form.name.trim(),
      rtspUrl: form.rtspUrl.trim(),
      groupId: form.groupId === '' ? null : Number(form.groupId),
      type: form.type,
      recordingMode: form.recordingMode,
      recordingEnabled: form.recordingEnabled,
      storageTarget: form.storageTarget,
      status: 'active' as const,
      isFavorite: false,
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      const saved = await camerasApi.create(payload);
      if (form.recordingEnabled && form.recordingMode === 'scheduled') {
        try {
          await camerasApi.putRecordingSchedule(saved.id, { slots: scheduleSlots });
        } catch (err) {
          const message =
            err instanceof HttpError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Failed to save recording schedule';
          setSubmitError(
            `Camera was created, but the weekly schedule could not be saved: ${message}`,
          );
          return;
        }
      }
      onSaved(saved);
    } catch (err) {
      const message =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to save camera';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const drawerWidth =
    form.recordingEnabled && form.recordingMode === 'scheduled'
      ? 'w-full max-w-4xl sm:max-w-4xl'
      : 'w-full max-w-md';

  const scheduleTimezone = browserTimeZone();

  return (
    <Drawer
      open={open}
      title="Add camera"
      description="Register a new camera. The MediaMTX path is auto-created on save. Set recording mode and weekly schedule here, or adjust later in Settings."
      onClose={submitting ? () => undefined : onClose}
      widthClass={drawerWidth}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-muted">
            {submitting ? 'Saving…' : 'All fields marked * are required.'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={clsx(
                'rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-foreground',
                'hover:border-accent disabled:opacity-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="camera-form"
              disabled={submitting}
              className={clsx(
                'rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white',
                'hover:bg-accent-hover disabled:opacity-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              {submitting ? 'Saving…' : 'Create camera'}
            </button>
          </div>
        </div>
      }
    >
      <form id="camera-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field label="Name" required error={errors.name}>
          {(id) => (
            <TextInput
              id={id}
              autoFocus
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              maxLength={NAME_MAX}
              placeholder="Front Door"
              invalid={Boolean(errors.name)}
              disabled={submitting}
            />
          )}
        </Field>

        <Field
          label="RTSP URL"
          required
          hint="Example: rtsp://user:pass@192.168.1.10:554/Streaming/Channels/101"
          error={errors.rtspUrl}
        >
          {(id) => (
            <TextInput
              id={id}
              value={form.rtspUrl}
              onChange={(e) => update('rtspUrl', e.target.value)}
              placeholder="rtsp://…"
              invalid={Boolean(errors.rtspUrl)}
              disabled={submitting}
              spellCheck={false}
            />
          )}
        </Field>

        <Field label="Group">
          {(id) => (
            <Select
              id={id}
              value={form.groupId}
              onChange={(e) => update('groupId', e.target.value)}
              disabled={submitting}
            >
              <option value="">— No group —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Type">
          {(id) => (
            <Select
              id={id}
              value={form.type}
              onChange={(e) => update('type', e.target.value as CameraType)}
              disabled={submitting}
            >
              <option value="standard">Standard</option>
              <option value="ptz">PTZ</option>
              <option value="360">360°</option>
            </Select>
          )}
        </Field>

        <hr className="border-border" />

        <Field
          label="Recording"
          hint="Turns recording ON/OFF for this camera. When OFF, no recording mode or schedule is applied."
        >
          {(id) => (
            <Select
              id={id}
              value={form.recordingEnabled ? 'on' : 'off'}
              onChange={(e) => update('recordingEnabled', e.target.value === 'on')}
              disabled={submitting}
            >
              <option value="off">Off</option>
              <option value="on">On</option>
            </Select>
          )}
        </Field>

        {form.recordingEnabled ? (
          <>
            <Field
              label="Recording mode"
              hint="Continuous records 24/7 when active. Scheduled uses the weekly grid below."
            >
              {(id) => (
                <Select
                  id={id}
                  value={form.recordingMode}
                  onChange={(e) => update('recordingMode', e.target.value as RecordingMode)}
                  disabled={submitting}
                >
                  <option value="continuous">Continuous (24/7)</option>
                  <option value="scheduled">Scheduled</option>
                </Select>
              )}
            </Field>

            {form.recordingMode === 'scheduled' ? (
              <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-2/50 p-3">
                <p className="text-xs font-medium text-foreground">Weekly recording schedule</p>
                <p className="text-[11px] leading-relaxed text-muted">
                  Slot times use the IANA zone{' '}
                  <span className="font-mono text-foreground">{scheduleTimezone}</span>. The server
                  stores the schedule in{' '}
                  <span className="font-mono">DEFAULT_RECORDING_SCHEDULE_TIMEZONE</span> when none
                  is set yet (default <span className="font-mono">Asia/Kolkata</span>).
                </p>
                <RecordingScheduleGrid value={scheduleSlots} onChange={setScheduleSlots} />
              </div>
            ) : null}
          </>
        ) : null}

        <Field label="Storage target">
          {(id) => (
            <Select
              id={id}
              value={form.storageTarget}
              onChange={(e) => update('storageTarget', e.target.value as StorageTarget)}
              disabled={submitting}
            >
              {/* <option value="nas">NAS</option> */}
              <option value="local">Local</option>
            </Select>
          )}
        </Field>

        {submitError && (
          <p
            role="alert"
            className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
          >
            {submitError}
          </p>
        )}
      </form>
    </Drawer>
  );
}
